import React, { useEffect, useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { TextField } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import clsx from 'clsx'
import AccountRouterLink from 'components/account-router-link'
import FormDialog from 'components/dialog'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import BookOutlinedIcon from 'components/material-symbol/icons/Book'
import CheckIcon from 'components/material-symbol/icons/Check'
import CollectionsBookmarkOutlinedIcon from 'components/material-symbol/icons/CollectionsBookmark'
import EventNoteOutlined from 'components/material-symbol/icons/EventNote'
import NavigateNextOutlinedIcon from 'components/material-symbol/icons/NavigateNext'
import ReceiptOutlinedIcon from 'components/material-symbol/icons/Receipt'
import useIconAccessor from 'components/lesson-type-category/icons'
import ActionButtons from 'pages/pga-coach/bookings/booking-types/booking-type-details-dialog/action-buttons'
import IconBadge from 'pages/pga-coach/bookings/booking-types/booking-type-details-dialog/icon-badge'
import { useStyles } from 'pages/pga-coach/bookings/booking-types/booking-type-details-dialog/styles'
import SubviewPageHeader from 'pages/pga-coach/bookings/events/event-details-dialog/subview-page-header'
import { getLessonPackStatusLabel } from 'pages/club/my-lesson-packs/lesson-pack-action-utils'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import { getOutlinedTextFieldWrapperStyles } from 'lib/form-field-styles'
import { useIsMobile } from 'lib/hooks'
import pluralize from 'lib/pluralize'
import { toFullTextDate, toFullTextDayDateWithTimezone } from 'utils/dateUtils'
import { useSnackbar } from 'notistack'

const UPDATE_LESSON_PACK = gql`
  mutation UpdateLessonPack(
    $lessonPackId: ID!
    $newCreditBalance: Int!
    $reason: String
  ) {
    updateLessonPack(
      lessonPackId: $lessonPackId
      newCreditBalance: $newCreditBalance
      reason: $reason
    ) {
      message
      success
    }
  }
`

const getLessonCreditsWithEnrollment = lessonCredits =>
  (lessonCredits || []).filter(credit => credit.enrollment)

const useEditFieldStyles = makeStyles(theme => ({
  formField: {
    ...getOutlinedTextFieldWrapperStyles(theme),
    marginTop: theme.spacing(2),
  },
}))

const EditLessonPackDialog = ({ isOpen, setIsOpen, booking, refetch }) => {
  const titleClasses = useDialogTitleStyles()
  const editClasses = useEditFieldStyles()
  const isMobile = useIsMobile()
  const [newCreditBalance, setNewCreditBalance] = useState(
    booking.remainingCredits,
  )
  const { enqueueSnackbar } = useSnackbar()
  const [updateLessonPack] = useMutation(UPDATE_LESSON_PACK)

  const handleClose = () => setIsOpen(false)

  const handleSave = () => {
    updateLessonPack({
      variables: {
        lessonPackId: booking.lessonPackId,
        newCreditBalance: newCreditBalance,
        reason: 'Lesson Pack Edited',
      },
    }).then(({ data }) => {
      if (data.updateLessonPack.success) {
        enqueueSnackbar(data.updateLessonPack.message, {
          variant: 'success',
        })
        refetch()
        handleClose()
      } else {
        enqueueSnackbar(data.updateLessonPack.message, {
          variant: 'error',
        })
      }
    })
  }

  return (
    <FormDialog
      isOpen={!!isOpen}
      onClose={handleClose}
      showCloseButton
      fullWidth
      maxWidth="xs"
      fullScreen={isMobile}
      classes={{ paper: titleClasses.dialogPaper }}
    >
      {() => (
        <>
          <DialogTitle
            disableTypography
            classes={{ root: titleClasses.titleRoot }}
          >
            <Typography component="h2" className={titleClasses.title}>
              Edit Lesson Pack
            </Typography>
          </DialogTitle>
          <DialogContent classes={{ root: titleClasses.content }}>
            <Typography className={titleClasses.bodyText}>
              Editing a lesson pack will add or remove lesson credits from their
              student account and they will receive an email confirmation.
            </Typography>
            <TextField
              id="lesson-pack-sessions-remaining"
              type="number"
              label="Sessions Remaining"
              fullWidth
              variant="outlined"
              className={editClasses.formField}
              value={newCreditBalance}
              onChange={e => {
                setNewCreditBalance(parseInt(e.target.value))
              }}
            />
          </DialogContent>
          <DialogActions className={titleClasses.actions}>
            <Button color="primary" size="large" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disableElevation
              startIcon={<CheckIcon fontSize="small" />}
              onClick={handleSave}
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </FormDialog>
  )
}

const LessonPackHistorySubview = ({ booking, onBack, readOnly }) => {
  const classes = useStyles()
  const lessonCreditsWithEnrollment = getLessonCreditsWithEnrollment(
    booking.lessonCredits,
  )

  return (
    <>
      <SubviewPageHeader pageTitle="Lesson Pack History" onBack={onBack} />
      <Box className={classes.detailsCard}>
        {lessonCreditsWithEnrollment.map((lessonCredit, index) => (
          <Box key={index} className={classes.detailRow}>
            <IconBadge icon={EventNoteOutlined} />
            <Box minWidth={0}>
              <Typography className={classes.detailPrimary}>
                {toFullTextDayDateWithTimezone(
                  lessonCredit.enrollment?.lesson.startDateTime,
                )}
              </Typography>
              {!readOnly ? (
                <Typography className={classes.detailSecondary}>
                  {lessonCredit.enrollment?.student.firstName}{' '}
                  {lessonCredit.enrollment?.student.lastName}
                </Typography>
              ) : null}
            </Box>
          </Box>
        ))}
      </Box>
    </>
  )
}

const LessonPacksDetailsDialogContent = ({
  booking,
  onClose,
  onOpenHistory,
  onEdit,
  onBack,
  view = 'detail',
  readOnly = false,
}) => {
  const {
    lessonCredits = [],
    expiresAt,
    remainingCredits,
    category,
    title,
    registrationPayments = [],
  } = booking
  const classes = useStyles()
  const accessor = useIconAccessor()
  const lessonCategory = accessor[category] || accessor.OTHER
  const lessonCreditsWithEnrollment = getLessonCreditsWithEnrollment(
    lessonCredits,
  )
  const lessonsPurchased = lessonCredits.length
  const historyCount = lessonCreditsWithEnrollment.length
  const registrationPayment = registrationPayments[0]
  const registrationPaymentId = registrationPayment?.id
  const statusLabel = getLessonPackStatusLabel({
    remainingCredits,
    expiresAt,
  })

  return (
    <>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.hero}>
          <ActionButtons
            setIsOpen={onClose}
            bookingType={booking}
            canEdit={!readOnly}
            onEdit={onEdit}
            readOnly={readOnly}
          />
          <Box className={classes.heroContent}>
            <Chip
              label={statusLabel}
              size="small"
              className={classes.statusChip}
            />
            <Typography className={classes.heroTitle}>{title}</Typography>
            <Typography className={classes.heroSubtitle}>
              {lessonCategory.text}
            </Typography>
          </Box>
          <Box className={classes.heroPrice}>
            <Typography className={classes.heroPriceLabel}>
              REMAINING
            </Typography>
            <Typography className={classes.heroPriceValue}>
              {remainingCredits}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.contentRoot}>
        {view === 'history' ? (
          <LessonPackHistorySubview
            booking={booking}
            onBack={onBack}
            readOnly={readOnly}
          />
        ) : (
          <Box className={classes.sidebar}>
            <Box className={classes.detailsCard}>
              <Typography className={classes.sectionLabel}>
                Lesson Pack Details
              </Typography>
              <Box className={classes.detailRow}>
                <IconBadge icon={CollectionsBookmarkOutlinedIcon} />
                <Box>
                  <Typography className={classes.detailPrimary}>
                    Number of Lessons Purchased
                  </Typography>
                  <Typography className={classes.detailSecondary}>
                    {lessonsPurchased} Lessons
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.detailRow}>
                <IconBadge icon={BookOutlinedIcon} />
                <Box>
                  <Typography className={classes.detailPrimary}>
                    Sessions Remaining
                  </Typography>
                  <Typography className={classes.detailSecondary}>
                    {remainingCredits} Lessons
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.detailRow}>
                <IconBadge icon={EventNoteOutlined} />
                <Box>
                  <Typography className={classes.detailPrimary}>
                    Expiration
                  </Typography>
                  <Typography className={classes.detailSecondary}>
                    {expiresAt
                      ? toFullTextDayDateWithTimezone(expiresAt)
                      : 'These packs do not expire'}
                  </Typography>
                </Box>
              </Box>
              {registrationPaymentId ? (
                <SensitiveInfoWrapper>
                  <Box
                    component={AccountRouterLink}
                    to={`/account/payments/transactions/${registrationPaymentId}`}
                    className={clsx(
                      classes.detailRow,
                      classes.clickableDetailRow,
                    )}
                    data-testid="lesson-pack-transaction-link"
                  >
                    <IconBadge icon={ReceiptOutlinedIcon} />
                    <Box flex={1} minWidth={0}>
                      <Typography className={classes.detailPrimary}>
                        Transaction
                      </Typography>
                      <Typography className={classes.detailSecondary}>
                        {registrationPayment?.createdAt
                          ? `Paid on ${toFullTextDate(
                            registrationPayment.createdAt,
                          )}`
                          : 'View receipt'}
                      </Typography>
                    </Box>
                    <NavigateNextOutlinedIcon color="action" />
                  </Box>
                </SensitiveInfoWrapper>
              ) : null}
            </Box>
            {historyCount > 0 ? (
              <Box
                className={classes.infoCard}
                onClick={onOpenHistory}
                role="button"
                tabIndex={0}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onOpenHistory()
                  }
                }}
              >
                <IconBadge icon={EventNoteOutlined} variant="action" />
                <Box className={classes.infoCardContent}>
                  <Typography className={classes.infoCardTitle}>
                    {historyCount} {pluralize(historyCount, 'session')}
                  </Typography>
                  <Typography className={classes.infoCardSubtitle}>
                    Lesson Pack History
                  </Typography>
                </Box>
                <NavigateNextOutlinedIcon color="action" />
              </Box>
            ) : null}
          </Box>
        )}
      </DialogContent>
    </>
  )
}

const LessonPackDetailsDialog = ({
  isOpen,
  setIsOpen,
  booking,
  refetch,
  readOnly = false,
}) => {
  const classes = useStyles()
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))
  const [view, setView] = useState('detail')
  const [isEditLessonPackOpen, setIsEditLessonPackOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setView('detail')
    }
  }, [isOpen])

  if (!booking) {
    return null
  }

  const handleClose = () => {
    setView('detail')
    setIsOpen(false)
  }

  return (
    <Dialog
      open={!!isOpen}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      classes={isMobile ? undefined : { paper: classes.dialogPaper }}
    >
      {isOpen ? (
        <LessonPacksDetailsDialogContent
          booking={booking}
          onClose={handleClose}
          onOpenHistory={() => setView('history')}
          onBack={() => setView('detail')}
          onEdit={() => setIsEditLessonPackOpen(true)}
          view={view}
          readOnly={readOnly}
        />
      ) : null}
      {!readOnly && isEditLessonPackOpen ? (
        <EditLessonPackDialog
          isOpen={isEditLessonPackOpen}
          setIsOpen={setIsEditLessonPackOpen}
          booking={booking}
          refetch={refetch}
        />
      ) : null}
    </Dialog>
  )
}

export default LessonPackDetailsDialog
